import {
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
} from 'config/appConfig'
import { PO_AUDIT_SESSION_ID_KEY } from 'src/utils/constant'

const PO_AUDIT_SESSION_KEY = PO_AUDIT_SESSION_ID_KEY

/**
 * Reads the `po_audit_sessionId` localStorage entry, normalizing it to an
 * `{ [offerId]: analyticsSessionId }` map.
 *
 * Backwards compatibility: if a legacy string value is found, it is returned
 * as an empty map (the legacy single-session is no longer reused).
 *
 * @returns {Object<string, string>} offerId → analytics sessionId map.
 */
export function getPOAnalyticsSessionMap() {
  const raw = getLocalStorage(PO_AUDIT_SESSION_KEY)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }
  return raw
}

/**
 * Returns the analytics sessionId associated with the given offerId, or ''.
 *
 * @param {string} offerId - Personal offer / pitch id.
 * @returns {string}
 */
export function getPOAnalyticsSessionId(offerId) {
  if (!offerId) return ''
  const map = getPOAnalyticsSessionMap()
  return map[offerId] || ''
}

/**
 * Stores the analytics sessionId for a given offerId by merging it into the
 * existing `po_audit_sessionId` map.
 *
 * @param {string} offerId
 * @param {string} sessionId
 */
export function setPOAnalyticsSessionId(offerId, sessionId) {
  if (!offerId || !sessionId) return
  const map = getPOAnalyticsSessionMap()
  map[offerId] = sessionId
  setLocalStorage(PO_AUDIT_SESSION_KEY, map)
}

/**
 * Removes the analytics sessionId entry for the given offerId. If the map
 * becomes empty, the entire `po_audit_sessionId` localStorage key is removed.
 *
 * @param {string} offerId
 */
export function removePOAnalyticsSessionId(offerId) {
  if (!offerId) return
  const map = getPOAnalyticsSessionMap()
  if (!(offerId in map)) return
  delete map[offerId]
  if (Object.keys(map).length === 0) {
    deleteFromLocalStorage(PO_AUDIT_SESSION_KEY)
  } else {
    setLocalStorage(PO_AUDIT_SESSION_KEY, map)
  }
}

/**
 * Clears the entire `po_audit_sessionId` localStorage entry. Used on logout.
 */
export function clearPOAnalyticsSessionMap() {
  deleteFromLocalStorage(PO_AUDIT_SESSION_KEY)
}
